import config from '../../../config';
import { CallStatus } from '../../enums';
import {
  DetailedAccount,
  PaymentReturnUrls,
  PaymentReturnUrlTypeEnum,
  PlanPhaseTypeEnum,
  PurchasePaymentTypeEnum,
  TradingPlan,
  TradingPlanStatus,
  TradingPlanTypeEnum,
} from '../../models/crm/types';
import {
  ENROLL_METHOD,
  ENROLL_REQUEST_STATE,
  IndividualExtendedInfoData,
} from '../../models/enroll';
import {
  getDemoTradingAccount,
  isClosedAccountStatus,
  isDemoAccountReference,
} from '../../util/account-helpers';
import { maxBy } from '../../util/collectionHelpers';
import { setCallStatus } from '../../util/error-handling/StatusByCallHelpers';
import { isEmpty } from '../../util/typeGuards';
import { NullableNumber, NullableString } from '../../util/types';
import { isExpirationPassed, toExpirationMoment } from '../account-flow/usability';
import { RootState } from '../index';

import { CRMState } from './index';
import { CRMCall } from './types';

export function parseOnboardingErrorCode(status: number) {
  if (status >= 400 && status <= 499) return ENROLL_REQUEST_STATE.REQUEST_ERROR;
  if (status >= 500 && status <= 599) return ENROLL_REQUEST_STATE.SERVER_ERROR;

  return ENROLL_REQUEST_STATE.ERROR;
}

export const getDefaultSelectableTradingPLanFromState = (state: CRMState): TradingPlan => {
  const activeOrPurchasableTypes = [
    TradingPlanStatus.Active,
    TradingPlanStatus.ActiveAndPurchasable,
    TradingPlanStatus.Purchasable,
  ];

  return state.tradingPlans.find(
    payloadItem => activeOrPurchasableTypes.includes(payloadItem.trading_plan_status_enum),
  ) ?? {} as TradingPlan;
};

export const getSelectedTradingPlan = (state: CRMState): TradingPlan|undefined => {
  const initiallPlanId: NullableNumber|NullableString = state.individualExtendedInfo?.initially_selected_plan_id;
  if (!initiallPlanId || !state.tradingPlans) {
    return undefined;
  }

  return state.tradingPlans.find(
    item => item.id === initiallPlanId,
  );
};

/**
 * Returns the latest non cash, non demo account of the response - the account ids are incremental,
 * so the highest one belongs to the most recently created account.
 */
export const getLatestTradingAccount = (accounts: DetailedAccount[]): DetailedAccount | undefined => maxBy(
  accounts.filter(account => !isDemoAccountReference(account.reference_id)),
  account => account.account_id,
);

const getRelevantAccounts = (accounts: DetailedAccount[]): DetailedAccount[] => {
  if (isEmpty(accounts)) {
    return [];
  }

  const openAccounts = accounts.filter(
    account => !isClosedAccountStatus(account.account_status),
  );
  if (!isEmpty(openAccounts)) {
    return openAccounts;
  }

  const latestClosedAccount = getLatestTradingAccount(accounts);
  return latestClosedAccount ? [ latestClosedAccount ] : [];
};

const getPlanFromStateByType = (
  state: CRMState,
  planType: TradingPlanTypeEnum,
  accounts?: DetailedAccount[],
): TradingPlan|undefined => {
  const tradingAccounts = accounts ?? state.detailedAccounts.tradingAccounts ?? [];
  const demoAccount = tradingAccounts.find(account => isDemoAccountReference(account.reference_id));
  const fundedAccounts = tradingAccounts.filter(account => !isDemoAccountReference(account.reference_id));

  const relevantFundedAccounts = getRelevantAccounts(fundedAccounts);
  const demoAccounts = demoAccount ? [ demoAccount ] : [];
  const relevantAccounts = [ ...relevantFundedAccounts, ...demoAccounts ];

  const planIds = relevantAccounts
    .map(account => account.trading_plan_id)
    .filter((planId): planId is number => planId != null);

  if (isEmpty(planIds) || isEmpty(state.tradingPlans)) {
    return undefined;
  }

  return state.tradingPlans.find(
    item => item.plan_type === planType && planIds.includes(item.id),
  );
};

/**
 * The reference id of the account the app is currently working with - the demo one when it is
 * selected, otherwise the selected trading account.
 */
export function getCurrentAccount(state: RootState): string {
  const { selectedAccount, isUseDemoAccount, tradingAccounts } = state.crm;
  if (isUseDemoAccount) {
    return getDemoTradingAccount(tradingAccounts) ?? '';
  }

  return tradingAccounts[selectedAccount];
}

export const getDetailedAccountByReference = (
  accounts: DetailedAccount[],
  accountReferenceId: NullableString,
): DetailedAccount | undefined => (
  accountReferenceId
    ? accounts.find(account => account.reference_id === accountReferenceId)
    : undefined
);

export const getCurrentDetailedAccount = (state: RootState): DetailedAccount|undefined => (
  getDetailedAccountByReference(state.crm.detailedAccounts.tradingAccounts, getCurrentAccount(state))
);

export const getAccountExpirationDate = (state: RootState): string|undefined => {
  const { account_expiration_date } = getCurrentDetailedAccount(state) ?? {};

  return toExpirationMoment(account_expiration_date) ?? undefined;
};

export const isCurrentAccountExpired = (state: RootState): boolean =>
  isExpirationPassed(getCurrentDetailedAccount(state)?.account_expiration_date);

export const getCurrentAccountTradingPlan = (state: CRMState): TradingPlan|undefined => {
  // Check if we have a pending purchased plan ID
  // BUT respect manual demo account selection if user has both accounts
  const hasPaidAndDemoPlan = hasAccountPaidAndDemoPlan(state);
  if (state.pendingPurchasedPlanId && !state.isUseDemoAccount) {
    const pendingPlan = state.tradingPlans.find(p => p.id === state.pendingPurchasedPlanId);
    if (pendingPlan) {
      return pendingPlan;
    }
  }

  const hasOnlyDemoPlan = hasAccountOnlyDemoPlan(state);
  if (hasOnlyDemoPlan) {
    return getPlanFromStateByType(state, TradingPlanTypeEnum.Demo);
  }

  const hasOnlyPaidPlan = hasAccountOnlyPaidPlan(state);
  if (hasOnlyPaidPlan) {
    return getPlanFromStateByType(state, TradingPlanTypeEnum.Tryout);
  }

  if (hasPaidAndDemoPlan) {
    const { isUseDemoAccount } = state;
    return getPlanFromStateByType(
      state,
      isUseDemoAccount ? TradingPlanTypeEnum.Demo : TradingPlanTypeEnum.Tryout,
    );
  }

  return undefined;
};

export const getPreviousAndNextTradingPlans = (state: CRMState):
 { nextPlan: TradingPlan | null, previousPlan: TradingPlan | null } => {
  const currentTradingPlan = getCurrentAccountTradingPlan(state);
  let nextPlan;
  let previousPlan;
  if (currentTradingPlan) {
    const nextPlanId = currentTradingPlan.next_trading_plan_id;
    const previousPlanId = currentTradingPlan.previous_trading_plan_id;
    if (nextPlanId) {
      nextPlan = state.tradingPlans.find(plan => plan.id === nextPlanId);
    }
    if (previousPlanId) {
      previousPlan = state.tradingPlans.find(plan => plan.id === previousPlanId);
    }
  }

  return {
    nextPlan: nextPlan ?? null,
    previousPlan: previousPlan ?? null,
  };
};

export const hasAnyAccountPaidPlan = (state: CRMState, accounts?: DetailedAccount[]): boolean =>
  !!getPlanFromStateByType(state, TradingPlanTypeEnum.Tryout, accounts);

export const hasNonClosedPaidPlan = (state: CRMState, accounts?: DetailedAccount[]): boolean => {
  const sourceAccounts = accounts ?? state.detailedAccounts.tradingAccounts ?? [];
  const openAccounts = sourceAccounts.filter(
    account => !isClosedAccountStatus(account.account_status),
  );
  return hasAnyAccountPaidPlan(state, openAccounts);
};

export const hasAnyAccountDemoPlan = (state: CRMState): boolean => state.detailedAccounts.tradingAccounts
  .some(account => isDemoAccountReference(account.reference_id));

export const getAllPaidPlans = (state: CRMState): TradingPlan[] => state.tradingPlans.filter(
  plan => plan.plan_type === TradingPlanTypeEnum.Tryout,
);

/**
 * Filters trading plans to only include purchasable Challenge phase plans.
 * Challenge phase plans are the first in a chain (no previous plan) and have pricing.
 */
const getChallengePlansOnly = (plans: TradingPlan[]): TradingPlan[] => plans.filter(plan => plan.plan_price !== null
    && plan.plan_price_ccy !== null
    && plan.previous_trading_plan_id === null);

/**
 * Extracts unique account sizes from trading plans, sorted ascending
 */
export const getUniqueAccountSizes = (plans: TradingPlan[]): number[] => {
  const challengePlans = getChallengePlansOnly(plans);
  const sizes = new Set(challengePlans.map(plan => plan.daily_hard_bp));
  return Array.from(sizes).sort((a, b) => a - b);
};

/**
 * Extracts unique max drawdown percentages from trading plans, sorted ascending
 */
export const getUniqueMaxDrawdowns = (plans: TradingPlan[]): number[] => {
  const challengePlans = getChallengePlansOnly(plans);
  const drawdowns = new Set(challengePlans.map(plan => plan.maximum_total_loss));
  return Array.from(drawdowns).sort((a, b) => a - b);
};

/**
 * Builds mapping of account sizes to available max drawdowns
 * Returns object like { "5000": [4, 6, 8], "10000": [4, 6, 8, 10] }
 */
export const getAccountSizesToMaxDrawdowns = (plans: TradingPlan[]): Record<string, number[]> => {
  const challengePlans = getChallengePlansOnly(plans);

  const mapping: Record<string, Set<number>> = {};

  challengePlans.forEach(plan => {
    const accountSize = String(plan.daily_hard_bp);
    if (!mapping[accountSize]) {
      mapping[accountSize] = new Set();
    }
    mapping[accountSize].add(plan.maximum_total_loss);
  });

  const result: Record<string, number[]> = {};
  Object.keys(mapping).sort((a, b) => Number(a) - Number(b)).forEach(key => {
    result[key] = Array.from(mapping[key]).sort((a, b) => a - b);
  });

  return result;
};

export const hasAccountPaidAndDemoPlan = (
  state: CRMState,
): boolean => hasAnyAccountDemoPlan(state) && hasAnyAccountPaidPlan(state);

export const hasAccountOnlyDemoPlan = (state: CRMState): boolean => {
  const { tradingAccounts } = state.detailedAccounts;
  return !isEmpty(tradingAccounts)
    && tradingAccounts.every(account => isDemoAccountReference(account.reference_id));
};

export const isPlanDemoType = (planId: number, state: CRMState): boolean => state.tradingPlans.filter(
  plan => planId === plan.id && plan.plan_type === TradingPlanTypeEnum.Demo
  && plan.trading_plan_status_enum === TradingPlanStatus.ActiveAndPurchasable,
).length === 1;
const hasAccountOnlyPaidPlan = (
  state: CRMState,
): boolean => !hasAnyAccountDemoPlan(state) && hasAnyAccountPaidPlan(state);

export function setCRMCallStatus(call: CRMCall, status: CallStatus, state: CRMState, errorCode?: number) {
  setCallStatus(call, CRMCall[call], status, state.statusByCall, errorCode, state.errorsLog);
}


export const isFirstLogin = (individual: IndividualExtendedInfoData | null): boolean => individual?.login_count === 1;

export const preparePaymentURLs = (
  purchaseType: PurchasePaymentTypeEnum,
  successPath: string,
  cancelPath: string,
  errorPath: string,
  planId?: number,
): PaymentReturnUrls => {
  const planIdParam = planId ? `&planId=${planId}` : '';
  return {
    return_url: `${config.baseURL}${successPath}?purchaseType=${purchaseType}&callbackReturnType=${PaymentReturnUrlTypeEnum.Success}${planIdParam}`,
    cancel_url: `${config.baseURL}${cancelPath}?purchaseType=${purchaseType}&callbackReturnType=${PaymentReturnUrlTypeEnum.Cancel}${planIdParam}`,
    fail_url: `${config.baseURL}${errorPath}?purchaseType=${purchaseType}&callbackReturnType=${PaymentReturnUrlTypeEnum.Fail}${planIdParam}`,
  };
};

export const normalisePlanPhaseKey = (phase: PlanPhaseTypeEnum): string => {
  switch (phase) {
    case PlanPhaseTypeEnum.challenge: return 'challenge';
    case PlanPhaseTypeEnum.entry: return 'entry';
    case PlanPhaseTypeEnum.pro: return 'pro';
    case PlanPhaseTypeEnum.elite: return 'elite';
    default: return '';
  }
};
